import type { z } from 'zod';

import * as C from './contracts';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

/**
 * Who may call a route (docs/03 §1, §2):
 * - `none`: public
 * - `any`: any verified Firebase token (user, anonymous guest or TV)
 * - `user`: a non-anonymous user token
 * - `tv`: a TV token (`kind='tv'`)
 * - `viewer` | `editor` | `admin`: CMS role ≥ the given one
 */
export type RouteAuth = 'none' | 'any' | 'user' | 'tv' | 'viewer' | 'editor' | 'admin';

export interface RouteDef {
  method: HttpMethod;
  /** Path relative to the API prefix, `:name` placeholders for params. */
  path: string;
  auth: RouteAuth;
  /** Path-param schema (server-side validation); keys must match the `:name`s in `path`. */
  params?: z.ZodTypeAny;
  query?: z.ZodTypeAny;
  body?: z.ZodTypeAny;
  /** `json` (default), `form` (multipart, FormData) or `binary` (raw bytes). */
  bodyKind?: 'json' | 'form' | 'binary';
  /**
   * `json` (default), `binary` (raw bytes) or `empty` (204 No Content). For `binary` and
   * `empty` the `response` schema is not applied to the payload.
   */
  responseKind?: 'json' | 'binary' | 'empty';
  response: z.ZodTypeAny;
}

/** Param names inferred from a path template: `'/a/:x/b/:y'` → `'x' | 'y'`. */
export type PathParamNames<P extends string> = P extends `${string}:${infer Name}/${infer Rest}`
  ? Name | PathParamNames<`/${Rest}`>
  : P extends `${string}:${infer Name}`
    ? Name
    : never;

export type PathParams<P extends string> = { [K in PathParamNames<P>]: string };

function route<const R extends RouteDef>(def: R): R {
  return def;
}

/**
 * Every endpoint the API actually mounts. Keys are what `createApiClient` exposes as methods,
 * so a route only belongs here once a handler exists — a declaration with no handler produced
 * a typed client method that answered 404 with no compile-time warning (API-41). The endpoints
 * of docs/03 §2 that are still phase 2 live in `PHASE2_ROUTES` below;
 * `apps/api/test/routeTable.test.ts` keeps both lists honest against the mounted app.
 */
export const routes = {
  // Health / meta
  health: route({ method: 'GET', path: '/health', auth: 'none', response: C.HealthResponse }),
  config: route({ method: 'GET', path: '/config', auth: 'none', response: C.PublicConfig }),

  // TV pairing
  pairStart: route({
    method: 'POST',
    path: '/tv/pair/start',
    auth: 'none',
    body: C.PairStartBody,
    response: C.PairStartResponse,
  }),
  pairExchange: route({
    method: 'POST',
    path: '/tv/pair/exchange',
    auth: 'none',
    body: C.PairExchangeBody,
    response: C.PairExchangeResponse,
  }),
  pairResume: route({
    method: 'POST',
    path: '/tv/pair/resume',
    auth: 'none',
    body: C.PairResumeBody,
    response: C.PairResumeResponse,
  }),
  tvTelemetry: route({
    method: 'POST',
    path: '/tv/telemetry',
    auth: 'tv',
    body: C.TvTelemetryBody,
    response: C.TvTelemetryResponse,
  }),
  pairPrecheck: route({
    method: 'GET',
    path: '/tv/pair/precheck',
    auth: 'none',
    query: C.PairPrecheckQuery,
    response: C.PairPrecheckResponse,
  }),
  pairApprove: route({
    method: 'POST',
    path: '/tv/pair/approve',
    auth: 'user',
    body: C.PairApproveBody,
    response: C.PairApproveResponse,
  }),
  pairDeny: route({
    method: 'POST',
    path: '/tv/pair/deny',
    auth: 'user',
    body: C.PairDenyBody,
    response: C.PairDenyResponse,
  }),

  // Me / profile
  me: route({ method: 'GET', path: '/me', auth: 'any', response: C.MeResponse }),
  mePatch: route({
    method: 'PATCH',
    path: '/me',
    auth: 'any',
    body: C.MePatchBody,
    response: C.MePatchResponse,
  }),
  mePhoto: route({
    method: 'POST',
    path: '/me/photo',
    auth: 'any',
    bodyKind: 'form',
    response: C.MePhotoResponse,
  }),
  meLogout: route({
    method: 'POST',
    path: '/me/logout',
    auth: 'any',
    responseKind: 'empty',
    response: C.MeLogoutResponse,
  }),
  meDelete: route({ method: 'DELETE', path: '/me', auth: 'user', response: C.MeDeleteResponse }),

  // Catalog
  home: route({ method: 'GET', path: '/home', auth: 'any', response: C.HomeResponse }),
  playlist: route({
    method: 'GET',
    path: '/playlists/:id',
    auth: 'any',
    params: C.IdParams,
    query: C.PlaylistSongsQuery,
    response: C.PlaylistResponse,
  }),
  artist: route({
    method: 'GET',
    path: '/artists/:id',
    auth: 'any',
    params: C.IdParams,
    query: C.PlaylistSongsQuery,
    response: C.ArtistResponse,
  }),
  song: route({
    method: 'GET',
    path: '/songs/:id',
    auth: 'any',
    params: C.IdParams,
    response: C.SongResponse,
  }),
  songPlay: route({
    method: 'GET',
    path: '/songs/:id/play',
    auth: 'tv',
    params: C.IdParams,
    query: C.PlayQuery,
    response: C.PlayResponse,
  }),
  search: route({
    method: 'GET',
    path: '/search',
    auth: 'none',
    query: C.SearchQuery,
    response: C.SearchResponse,
  }),

  // Standalone web (docs/13): public and the same for every caller
  webHome: route({ method: 'GET', path: '/web/home', auth: 'none', response: C.WebHomeResponse }),
  webFree: route({ method: 'GET', path: '/web/free', auth: 'none', response: C.WebFreeResponse }),
  webFreeSongs: route({
    method: 'GET',
    path: '/web/free/songs',
    auth: 'none',
    query: C.PlaylistSongsQuery,
    response: C.WebSongPage,
  }),
  webSong: route({
    method: 'GET',
    path: '/web/songs/:idOrSlug',
    auth: 'none',
    params: C.WebSongParams,
    response: C.WebSongResponse,
  }),
  webPlaylist: route({
    method: 'GET',
    path: '/web/playlists/:id',
    auth: 'none',
    params: C.IdParams,
    query: C.PlaylistSongsQuery,
    response: C.WebPlaylistResponse,
  }),
  webArtist: route({
    method: 'GET',
    path: '/web/artists/:id',
    auth: 'none',
    params: C.IdParams,
    query: C.PlaylistSongsQuery,
    response: C.WebArtistResponse,
  }),

  // Standalone web: favorites and history (registered accounts only)
  meLibrary: route({
    method: 'GET',
    path: '/me/library',
    auth: 'user',
    response: C.LibrarySummary,
  }),
  meFavorites: route({
    method: 'GET',
    path: '/me/favorites',
    auth: 'user',
    query: C.LibraryQuery,
    response: C.FavoritesResponse,
  }),
  meFavoritesClear: route({
    method: 'DELETE',
    path: '/me/favorites',
    auth: 'user',
    response: C.LibraryOk,
  }),
  meFavoriteIds: route({
    method: 'GET',
    path: '/me/favorites/ids',
    auth: 'user',
    response: C.FavoriteIdsResponse,
  }),
  meFavoriteAdd: route({
    method: 'PUT',
    path: '/me/favorites/:songId',
    auth: 'user',
    params: C.LibrarySongParams,
    response: C.LibraryOk,
  }),
  meFavoriteRemove: route({
    method: 'DELETE',
    path: '/me/favorites/:songId',
    auth: 'user',
    params: C.LibrarySongParams,
    response: C.LibraryOk,
  }),
  meHistory: route({
    method: 'GET',
    path: '/me/history',
    auth: 'user',
    query: C.LibraryQuery,
    response: C.HistoryResponse,
  }),
  meHistoryClear: route({
    method: 'DELETE',
    path: '/me/history',
    auth: 'user',
    response: C.LibraryOk,
  }),
  meHistoryRemove: route({
    method: 'DELETE',
    path: '/me/history/:songId',
    auth: 'user',
    params: C.LibrarySongParams,
    response: C.LibraryOk,
  }),

  // Standalone web: the browser as the screen
  webSessionCreate: route({
    method: 'POST',
    path: '/web/sessions',
    auth: 'user',
    body: C.WebSessionBody,
    response: C.WebSessionResponse,
  }),
  webSessionCurrent: route({
    method: 'GET',
    path: '/web/sessions/current',
    auth: 'user',
    query: C.WebSessionCurrentQuery,
    response: C.WebSessionCurrentResponse,
  }),
  webSessionPatch: route({
    method: 'PATCH',
    path: '/web/sessions/:sid',
    auth: 'user',
    params: C.SessionParams,
    body: C.WebSessionPatchBody,
    response: C.WebSessionPatchResponse,
  }),

  // Sessions & queue
  session: route({
    method: 'GET',
    path: '/sessions/:sid',
    auth: 'any',
    params: C.SessionParams,
    response: C.SessionResponse,
  }),
  sessionJoin: route({
    method: 'POST',
    path: '/sessions/join',
    auth: 'any',
    body: C.SessionJoinBody,
    response: C.SessionJoinResponse,
  }),
  /**
   * Must stay **mounted before** `GET /sessions/:sid`, or `precheck` is read as a session id.
   * Auth is `any`: `/guest` signs the visitor in anonymously before it asks, so join codes
   * cannot be enumerated without a Firebase token at all.
   */
  sessionPrecheck: route({
    method: 'GET',
    path: '/sessions/precheck',
    auth: 'any',
    query: C.SessionPrecheckQuery,
    response: C.SessionPrecheckResponse,
  }),

  sessionLeave: route({
    method: 'POST',
    path: '/sessions/:sid/leave',
    auth: 'any',
    params: C.SessionParams,
    response: C.SessionLeaveResponse,
  }),
  enqueue: route({
    method: 'POST',
    path: '/sessions/:sid/queue',
    auth: 'any',
    params: C.SessionParams,
    body: C.EnqueueBody,
    response: C.EnqueueResponse,
  }),
  playNowItem: route({
    method: 'POST',
    path: '/sessions/:sid/queue/:itemId/play-now',
    auth: 'any',
    params: C.QueueItemParams,
    response: C.PlayNowItemResponse,
  }),
  playNow: route({
    method: 'POST',
    path: '/sessions/:sid/play-now',
    auth: 'any',
    params: C.SessionParams,
    body: C.PlayNowBody,
    response: C.PlayNowResponse,
  }),
  removeQueueItem: route({
    method: 'DELETE',
    path: '/sessions/:sid/queue/:itemId',
    auth: 'any',
    params: C.QueueItemParams,
    response: C.RemoveQueueItemResponse,
  }),
  updateQueueItem: route({
    method: 'PATCH',
    path: '/sessions/:sid/queue/:itemId',
    auth: 'any',
    params: C.QueueItemParams,
    body: C.QueueItemPatchBody,
    response: C.QueueItemPatchResponse,
  }),
  reorderQueue: route({
    method: 'PATCH',
    path: '/sessions/:sid/queue/reorder',
    auth: 'any',
    params: C.SessionParams,
    body: C.ReorderBody,
    response: C.ReorderResponse,
  }),
  playerCommand: route({
    method: 'POST',
    path: '/sessions/:sid/player/command',
    auth: 'any',
    params: C.SessionParams,
    body: C.PlayerCommandBody,
    response: C.PlayerCommandResponse,
  }),
  playback: route({
    method: 'POST',
    path: '/sessions/:sid/playback',
    auth: 'tv',
    params: C.SessionParams,
    body: C.PlaybackBody,
    response: C.PlaybackResponse,
  }),
  sessionQr: route({
    method: 'GET',
    path: '/sessions/:sid/qr',
    auth: 'any',
    params: C.SessionParams,
    response: C.SessionQrResponse,
  }),

  // Billing
  billingVoucher: route({
    method: 'POST',
    path: '/billing/voucher',
    auth: 'user',
    body: C.VoucherBody,
    response: C.VoucherResponse,
  }),
  // Public: the plans screens show prices to visitors (docs/openapi-web.yaml).
  billingProducts: route({
    method: 'GET',
    path: '/billing/products',
    auth: 'none',
    response: C.ProductsResponse,
  }),
  billingCheckout: route({
    method: 'POST',
    path: '/billing/checkout',
    auth: 'user',
    body: C.CheckoutBody,
    response: C.CheckoutResponse,
  }),
  billingPortal: route({
    method: 'POST',
    path: '/billing/portal',
    auth: 'user',
    body: C.PortalBody,
    response: C.PortalResponse,
  }),
  stripeWebhook: route({
    method: 'POST',
    path: '/webhooks/stripe',
    auth: 'none',
    bodyKind: 'binary',
    response: C.StripeWebhookResponse,
  }),

  // CMS — artists
  cmsArtists: route({
    method: 'GET',
    path: '/cms/artists',
    auth: 'viewer',
    query: C.CmsArtistsQuery,
    response: C.CmsArtistsResponse,
  }),
  cmsArtistCreate: route({
    method: 'POST',
    path: '/cms/artists',
    auth: 'editor',
    body: C.CmsArtistCreateBody,
    response: C.CmsArtistResponse,
  }),
  cmsArtist: route({
    method: 'GET',
    path: '/cms/artists/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsArtistResponse,
  }),
  cmsArtistPatch: route({
    method: 'PATCH',
    path: '/cms/artists/:id',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsArtistPatchBody,
    response: C.CmsArtistResponse,
  }),
  cmsArtistDelete: route({
    method: 'DELETE',
    path: '/cms/artists/:id',
    auth: 'editor',
    params: C.IdParams,
    response: C.ApiOk,
  }),

  // CMS — songs
  cmsSongs: route({
    method: 'GET',
    path: '/cms/songs',
    auth: 'viewer',
    query: C.CmsSongsQuery,
    response: C.CmsSongsResponse,
  }),
  cmsSongCreate: route({
    method: 'POST',
    path: '/cms/songs',
    auth: 'editor',
    body: C.CmsSongCreateBody,
    response: C.CmsSongResponse,
  }),
  cmsSongGenres: route({
    method: 'GET',
    path: '/cms/songs/genres',
    auth: 'viewer',
    response: C.CmsSongGenresResponse,
  }),
  cmsSong: route({
    method: 'GET',
    path: '/cms/songs/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsSongResponse,
  }),
  cmsSongPatch: route({
    method: 'PATCH',
    path: '/cms/songs/:id',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsSongPatchBody,
    response: C.CmsSongResponse,
  }),
  cmsSongDelete: route({
    method: 'DELETE',
    path: '/cms/songs/:id',
    auth: 'editor',
    params: C.IdParams,
    response: C.ApiOk,
  }),
  cmsSongTonesPut: route({
    method: 'PUT',
    path: '/cms/songs/:id/tones',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsTonesPutBody,
    response: C.CmsSongResponse,
  }),
  cmsSongPublish: route({
    method: 'POST',
    path: '/cms/songs/:id/publish',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsSongResponse,
  }),
  cmsSongUnpublish: route({
    method: 'POST',
    path: '/cms/songs/:id/unpublish',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsSongResponse,
  }),
  /**
   * Phase-1 media check (docs/07 §4): the catalog is progressive MP4, so this HEADs the source
   * object in R2 instead of verifying an HLS package.
   */
  cmsSongMediaCheck: route({
    method: 'POST',
    path: '/cms/songs/:id/media/check',
    auth: 'editor',
    params: C.IdParams,
    response: C.SongMediaCheckResponse,
  }),
  /**
   * Queues an on-demand tone rendition (docs/07 §4, WP4.0): upserts the pending
   * `derived_assets` row the tone worker picks up. Idempotent per song + tone.
   */
  cmsSongDerive: route({
    method: 'POST',
    path: '/cms/songs/:id/derive',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsSongDeriveBody,
    response: C.CmsSongDeriveResponse,
  }),
  /** Copies the Deezer cover (`songs.cover_url`) into R2 and points `image_path` at it. */
  cmsSongDeezerCover: route({
    method: 'POST',
    path: '/cms/songs/:id/cover/deezer',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsSongResponse,
  }),

  // CMS — uploads
  cmsUploadSign: route({
    method: 'POST',
    path: '/cms/uploads/sign',
    auth: 'editor',
    body: C.UploadSignBody,
    response: C.UploadSignResponse,
  }),
  /** Worker-proxied PUT used when the Worker has no R2 S3 credentials (docs/06 §3). */
  cmsUploadProxy: route({
    method: 'PUT',
    path: '/cms/uploads/proxy',
    auth: 'editor',
    query: C.UploadProxyQuery,
    bodyKind: 'binary',
    response: C.UploadProxyResponse,
  }),

  // CMS — playlists
  cmsPlaylists: route({
    method: 'GET',
    path: '/cms/playlists',
    auth: 'viewer',
    query: C.CmsPlaylistsQuery,
    response: C.CmsPlaylistsResponse,
  }),
  cmsPlaylistCreate: route({
    method: 'POST',
    path: '/cms/playlists',
    auth: 'editor',
    body: C.CmsPlaylistCreateBody,
    response: C.CmsPlaylistResponse,
  }),
  cmsPlaylist: route({
    method: 'GET',
    path: '/cms/playlists/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsPlaylistResponse,
  }),
  cmsPlaylistPatch: route({
    method: 'PATCH',
    path: '/cms/playlists/:id',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsPlaylistPatchBody,
    response: C.CmsPlaylistResponse,
  }),
  cmsPlaylistDelete: route({
    method: 'DELETE',
    path: '/cms/playlists/:id',
    auth: 'editor',
    params: C.IdParams,
    response: C.ApiOk,
  }),
  cmsPlaylistSongsPut: route({
    method: 'PUT',
    path: '/cms/playlists/:id/songs',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsPlaylistSongsPutBody,
    response: C.CmsPlaylistResponse,
  }),

  // CMS — heroes
  cmsHeroes: route({
    method: 'GET',
    path: '/cms/heroes',
    auth: 'viewer',
    query: C.CmsHeroesQuery,
    response: C.CmsHeroesResponse,
  }),
  cmsHeroCreate: route({
    method: 'POST',
    path: '/cms/heroes',
    auth: 'editor',
    body: C.CmsHeroCreateBody,
    response: C.CmsHeroResponse,
  }),
  cmsHero: route({
    method: 'GET',
    path: '/cms/heroes/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsHeroResponse,
  }),
  cmsHeroPatch: route({
    method: 'PATCH',
    path: '/cms/heroes/:id',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsHeroPatchBody,
    response: C.CmsHeroResponse,
  }),
  cmsHeroDelete: route({
    method: 'DELETE',
    path: '/cms/heroes/:id',
    auth: 'editor',
    params: C.IdParams,
    response: C.ApiOk,
  }),

  // CMS — home
  cmsHome: route({ method: 'GET', path: '/cms/home', auth: 'viewer', response: C.CmsHomeResponse }),
  cmsHomePut: route({
    method: 'PUT',
    path: '/cms/home',
    auth: 'editor',
    body: C.CmsHomePutBody,
    response: C.CmsHomePutResponse,
  }),
  cmsHomePublish: route({
    method: 'POST',
    path: '/cms/home/publish',
    auth: 'editor',
    response: C.CmsHomePublishResponse,
  }),
  cmsHomePreview: route({
    method: 'GET',
    path: '/cms/home/preview',
    auth: 'viewer',
    response: C.CmsHomePreviewResponse,
  }),
  /** "Discard draft" of CMS-08: drops the draft rows, leaving the published layout alone. */
  cmsHomeDraftDelete: route({
    method: 'DELETE',
    path: '/cms/home/draft',
    auth: 'editor',
    response: C.CmsHomeResponse,
  }),

  // CMS — catalog review queue (CMS-15, WP3.5)
  cmsReviews: route({
    method: 'GET',
    path: '/cms/review',
    auth: 'viewer',
    query: C.CmsReviewQuery,
    response: C.CmsReviewResponse,
  }),
  /** Writes the candidate ids (MBID, ISRC, Deezer id) and the cover onto the song. */
  cmsReviewAccept: route({
    method: 'POST',
    path: '/cms/review/:id/accept',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsReviewActionResponse,
  }),
  /** Exchanges title and credited artists, re-linking (or creating) the artist rows. */
  cmsReviewSwap: route({
    method: 'POST',
    path: '/cms/review/:id/swap',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsReviewActionResponse,
  }),
  /** Tags the credited artists `placeholder` — the apps then render the song with no artist. */
  cmsReviewNoArtist: route({
    method: 'POST',
    path: '/cms/review/:id/no-artist',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsReviewActionResponse,
  }),
  cmsReviewIgnore: route({
    method: 'POST',
    path: '/cms/review/:id/ignore',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsReviewActionResponse,
  }),

  // CMS — users (admin)
  cmsUsers: route({
    method: 'GET',
    path: '/cms/users',
    auth: 'admin',
    query: C.CmsUsersQuery,
    response: C.CmsUsersResponse,
  }),
  cmsUser: route({
    method: 'GET',
    path: '/cms/users/:id',
    auth: 'admin',
    params: C.CmsUserParams,
    response: C.CmsUserDetailResponse,
  }),
  cmsUserPatch: route({
    method: 'PATCH',
    path: '/cms/users/:id',
    auth: 'admin',
    params: C.CmsUserParams,
    body: C.CmsUserPatchBody,
    response: C.CmsUserResponse,
  }),

  // CMS — grants
  cmsGrants: route({
    method: 'GET',
    path: '/cms/grants',
    auth: 'viewer',
    query: C.CmsGrantsQuery,
    response: C.CmsGrantsResponse,
  }),
  cmsGrantCreate: route({
    method: 'POST',
    path: '/cms/grants',
    auth: 'editor',
    body: C.CmsGrantCreateBody,
    response: C.CmsGrantCreateResponse,
  }),
  cmsGrant: route({
    method: 'GET',
    path: '/cms/grants/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsGrantDetailResponse,
  }),
  cmsGrantPatch: route({
    method: 'PATCH',
    path: '/cms/grants/:id',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsGrantPatchBody,
    response: C.CmsGrantResponse,
  }),
  cmsGrantDelete: route({
    method: 'DELETE',
    path: '/cms/grants/:id',
    auth: 'editor',
    params: C.IdParams,
    response: C.CmsGrantDeleteResponse,
  }),
  cmsGrantRevoke: route({
    method: 'POST',
    path: '/cms/grants/:id/revoke',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsGrantRevokeBody,
    response: C.CmsGrantRevokeResponse,
  }),

  // CMS — sessions
  cmsSessions: route({
    method: 'GET',
    path: '/cms/sessions',
    auth: 'viewer',
    query: C.CmsSessionsQuery,
    response: C.CmsSessionsResponse,
  }),
  cmsSession: route({
    method: 'GET',
    path: '/cms/sessions/:id',
    auth: 'viewer',
    params: C.IdParams,
    response: C.CmsSessionDetailResponse,
  }),
  cmsSessionClose: route({
    method: 'POST',
    path: '/cms/sessions/:id/close',
    auth: 'editor',
    params: C.IdParams,
    body: C.CmsSessionCloseBody,
    response: C.CmsSessionCloseResponse,
  }),

  // CMS — devices
  cmsDevices: route({
    method: 'GET',
    path: '/cms/devices',
    auth: 'viewer',
    query: C.CmsDevicesQuery,
    response: C.CmsDevicesResponse,
  }),
  cmsDevice: route({
    method: 'GET',
    path: '/cms/devices/:id',
    auth: 'viewer',
    params: C.DeviceParams,
    response: C.CmsDeviceDetailResponse,
  }),
  cmsDevicePatch: route({
    method: 'PATCH',
    path: '/cms/devices/:id',
    auth: 'admin',
    params: C.DeviceParams,
    body: C.CmsDevicePatchBody,
    response: C.CmsDeviceResponse,
  }),

  // CMS — audit & stats
  cmsAudit: route({
    method: 'GET',
    path: '/cms/audit',
    auth: 'viewer',
    query: C.CmsAuditQuery,
    response: C.CmsAuditResponse,
  }),
  cmsStatsOverview: route({
    method: 'GET',
    path: '/cms/stats/overview',
    auth: 'viewer',
    response: C.CmsStatsOverviewResponse,
  }),

  // CMS — settings (admin)
  cmsSettings: route({
    method: 'GET',
    path: '/cms/settings',
    auth: 'admin',
    response: C.CmsSettingsResponse,
  }),
  cmsSettingsPut: route({
    method: 'PUT',
    path: '/cms/settings',
    auth: 'admin',
    body: C.CmsSettingsPutBody,
    response: C.CmsSettingsResponse,
  }),
} as const;

export type Routes = typeof routes;
export type RouteKey = keyof Routes;

export const ROUTE_KEYS = Object.keys(routes) as RouteKey[];

/**
 * Specified in docs/03 §2 but **not implemented yet** (phase 2: the CMS billing screens, and
 * EZDRM licence proxying, docs/07 §2/§4). They are kept here — same shape as `routes`, same
 * zod contracts — so the spec stays in code and phase 2 only has to move the entries across,
 * but they are deliberately out of `routes`: nothing generates a client method for a path the
 * server answers 404 on.
 */
export const PHASE2_ROUTES = {
  drmWidevine: route({
    method: 'POST',
    path: '/drm/widevine/:songId',
    auth: 'tv',
    params: C.DrmParams,
    bodyKind: 'binary',
    responseKind: 'binary',
    response: C.ApiOk,
  }),
  drmKeys: route({
    method: 'GET',
    path: '/keys/:songId',
    auth: 'tv',
    params: C.KeysParams,
    responseKind: 'binary',
    response: C.ApiOk,
  }),
  cmsProducts: route({
    method: 'GET',
    path: '/cms/products',
    auth: 'admin',
    query: C.CmsProductsQuery,
    response: C.CmsProductsResponse,
  }),
  cmsProductCreate: route({
    method: 'POST',
    path: '/cms/products',
    auth: 'admin',
    body: C.CmsProductCreateBody,
    response: C.CmsProductResponse,
  }),
  cmsProduct: route({
    method: 'GET',
    path: '/cms/products/:id',
    auth: 'admin',
    params: C.ProductParams,
    response: C.CmsProductResponse,
  }),
  cmsProductPatch: route({
    method: 'PATCH',
    path: '/cms/products/:id',
    auth: 'admin',
    params: C.ProductParams,
    body: C.CmsProductPatchBody,
    response: C.CmsProductResponse,
  }),
  cmsProductDelete: route({
    method: 'DELETE',
    path: '/cms/products/:id',
    auth: 'admin',
    params: C.ProductParams,
    response: C.CmsProductDeleteResponse,
  }),
  cmsPriceCreate: route({
    method: 'POST',
    path: '/cms/products/:id/prices',
    auth: 'admin',
    params: C.ProductParams,
    body: C.CmsPriceCreateBody,
    response: C.CmsPriceResponse,
  }),
  cmsPricePatch: route({
    method: 'PATCH',
    path: '/cms/prices/:id',
    auth: 'admin',
    params: C.IdParams,
    body: C.CmsPricePatchBody,
    response: C.CmsPriceResponse,
  }),
  cmsPriceDelete: route({
    method: 'DELETE',
    path: '/cms/prices/:id',
    auth: 'admin',
    params: C.IdParams,
    response: C.ApiOk,
  }),
  cmsProductSyncStripe: route({
    method: 'POST',
    path: '/cms/products/:id/sync-stripe',
    auth: 'admin',
    params: C.ProductParams,
    response: C.CmsProductSyncStripeResponse,
  }),
  cmsSubscriptions: route({
    method: 'GET',
    path: '/cms/subscriptions',
    auth: 'admin',
    query: C.CmsSubscriptionsQuery,
    response: C.CmsSubscriptionsResponse,
  }),
  cmsSubscriptionCreate: route({
    method: 'POST',
    path: '/cms/subscriptions',
    auth: 'admin',
    body: C.CmsSubscriptionCreateBody,
    response: C.CmsSubscriptionResponse,
  }),
  cmsSubscriptionCancel: route({
    method: 'POST',
    path: '/cms/subscriptions/:id/cancel',
    auth: 'admin',
    params: C.IdParams,
    body: C.CmsSubscriptionCancelBody,
    response: C.CmsSubscriptionCancelResponse,
  }),
} as const satisfies Record<string, RouteDef>;

export type Phase2RouteKey = keyof typeof PHASE2_ROUTES;

export const PHASE2_ROUTE_KEYS = Object.keys(PHASE2_ROUTES) as Phase2RouteKey[];

/** Query type accepted by the client for a route (`z.input`, so defaults stay optional). */
export type RouteQuery<R> = R extends { query: infer Q extends z.ZodTypeAny } ? z.input<Q> : never;
/** JSON body accepted by the client for a route. */
export type RouteBody<R> = R extends { body: infer B extends z.ZodTypeAny } ? z.input<B> : never;
/** Parsed JSON response of a route. */
export type RouteResponse<R> = R extends { response: infer S extends z.ZodTypeAny }
  ? z.infer<S>
  : never;

/** Replaces `:name` placeholders; throws when a param is missing. */
export function buildPath(path: string, params?: Record<string, string>): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_m, name: string) => {
    const value = params?.[name];
    if (value === undefined || value === '') {
      throw new TypeError(`Missing path param "${name}" for ${path}`);
    }
    return encodeURIComponent(value);
  });
}

/** Serializes a query object: skips undefined/null, repeats keys for arrays. */
export function buildQuery(query?: Record<string, unknown>): string {
  if (!query) return '';
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      for (const v of value) if (v !== undefined && v !== null) qs.append(key, String(v));
    } else {
      qs.set(key, String(value));
    }
  }
  const s = qs.toString();
  return s ? `?${s}` : '';
}
